import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, ArrowUp, ArrowDown, X } from "lucide-react";
import { ContactDetails } from "../ParcelBookingForm";
import ContactSection from "./ContactSection";
import { useIsMobile } from "@/hooks/use-mobile";
interface MultiContactSectionProps {
  title: string;
  contacts: ContactDetails[];
  onContactsChange: (contacts: ContactDetails[] | ((prev: ContactDetails[]) => ContactDetails[])) => void;
  prefix: "collection" | "delivery";
}
const MultiContactSection = ({
  title,
  contacts,
  onContactsChange,
  prefix
}: MultiContactSectionProps) => {
  const isMobile = useIsMobile();
  const handleContactChange = (index: number, field: string, value: any) => {
    console.log(`🔄 CONTACT CHANGE: ${prefix}[${index}].${field} = ${value}`);

    // Use functional update to ensure we have the latest state
    onContactsChange(currentContacts => {
      const updatedContacts = [...currentContacts];
      updatedContacts[index] = {
        ...updatedContacts[index],
        [field]: value
      };
      console.log(`✅ UPDATED CONTACTS for ${prefix}:`, updatedContacts.map((c, i) => `[${i}]: ${c.name}`));
      return updatedContacts;
    });
  };

  // New atomic contact update handler
  const handleContactUpdate = (index: number, updates: Partial<ContactDetails>) => {
    console.log(`🔄 ATOMIC CONTACT UPDATE: ${prefix}[${index}]`, updates);

    // Use functional update to ensure we have the latest state
    onContactsChange(currentContacts => {
      const updatedContacts = [...currentContacts];
      updatedContacts[index] = {
        ...updatedContacts[index],
        ...updates
      };
      console.log(`✅ ATOMIC UPDATE COMPLETE for ${prefix}:`, updatedContacts.map((c, i) => `[${i}]: ${c.name}`));
      return updatedContacts;
    });
  };
  const handleInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    console.log(`📝 INPUT CHANGE: ${prefix}[${index}].${name} = "${value}"`);
    handleContactChange(index, name, value);
  };
  const handlePhoneChange = (index: number, value: string) => {
    console.log(`📞 PHONE CHANGE: ${prefix}[${index}].phone = "${value || ''}"`);
    handleContactChange(index, 'phone', value || '');
  };
  const handleContactFieldChange = (index: number, field: string, value: string) => {
    console.log(`🎯 DIRECT FIELD CHANGE: ${prefix}[${index}].${field} = "${value}"`);
    handleContactChange(index, field, value);
  };
  const handleAddContact = () => {
    const newContact: ContactDetails = {
      isMyself: false,
      name: "",
      phone: "",
      email: "",
      isPrimary: contacts.length === 0,
      // First contact is primary by default
      sortOrder: contacts.length
    };
    onContactsChange([...contacts, newContact]);
  };
  const handleRemoveContact = (index: number) => {
    if (contacts.length <= 1) return; // Don't allow removing the last contact

    const updatedContacts = contacts.filter((_, i) => i !== index);
    // If we removed the primary contact, make the first one primary
    if (contacts[index].isPrimary && updatedContacts.length > 0) {
      updatedContacts[0].isPrimary = true;
    }
    // Update sort orders
    updatedContacts.forEach((contact, i) => {
      contact.sortOrder = i;
    });
    onContactsChange(updatedContacts);
  };
  const handleSetPrimary = (index: number) => {
    const updatedContacts = contacts.map((contact, i) => ({
      ...contact,
      isPrimary: i === index
    }));
    onContactsChange(updatedContacts);
  };
  const handleMoveContact = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === contacts.length - 1) {
      return;
    }
    const updatedContacts = [...contacts];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap contacts
    [updatedContacts[index], updatedContacts[targetIndex]] = [updatedContacts[targetIndex], updatedContacts[index]];

    // Update sort orders
    updatedContacts.forEach((contact, i) => {
      contact.sortOrder = i;
    });
    onContactsChange(updatedContacts);
  };
  const handleClearContact = (index: number) => {
    console.log(`🧹 CLEARING CONTACT: ${prefix}[${index}]`);
    const clearedContact: ContactDetails = {
      isMyself: false,
      name: "",
      phone: "",
      email: "",
      isPrimary: contacts[index].isPrimary,
      sortOrder: contacts[index].sortOrder
    };
    const updatedContacts = [...contacts];
    updatedContacts[index] = clearedContact;
    console.log(`✅ CLEARED CONTACT: ${prefix}[${index}]`, clearedContact);
    onContactsChange(updatedContacts);
  };
  return <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{title}</h2>
        <Button type="button" variant="outline" size="sm" onClick={handleAddContact} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {!isMobile && "Add Contact"}
        </Button>
      </div>

      {contacts.map((contact, index) => <Card key={index} className="p-4 border border-border relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">
                Contact {index + 1}
                {contact.isPrimary && <span className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                    Primary
                  </span>}
              </Label>
            </div>
            
            <div className="flex items-center gap-1">
              {!contact.isPrimary && <Button type="button" variant="ghost" size="sm" onClick={() => handleSetPrimary(index)} className="text-xs h-7">Set As Primary</Button>}
              
              {contacts.length > 1 && index > 0 && <Button type="button" variant="ghost" size="sm" onClick={() => handleMoveContact(index, 'up')}>
                  <ArrowUp className="h-3 w-3" />
                </Button>}
              
              {contacts.length > 1 && index < contacts.length - 1 && <Button type="button" variant="ghost" size="sm" onClick={() => handleMoveContact(index, 'down')}>
                  <ArrowDown className="h-3 w-3" />
                </Button>}
              
              {contacts.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveContact(index)} className="text-destructive hover:text-destructive">
                  <X className="h-3 w-3" />
                </Button>}
            </div>
          </div>

          <ContactSection title="" contact={contact} onContactChange={e => handleInputChange(index, e)} onPhoneChange={value => handlePhoneChange(index, value)} onClear={() => handleClearContact(index)} inputPrefix={`${prefix}-${index}`} hideTitle={true} contactIndex={index} onContactFieldChange={(field, value) => handleContactFieldChange(index, field, value)} onContactUpdate={updates => handleContactUpdate(index, updates)} />
        </Card>)}
    </div>;
};
export default MultiContactSection;